import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { CreativeFormatKey, CurrentTerms, PricingModel, TargetingPublisher } from "../../api/types";
import { useSession } from "../../auth/AuthProvider";
import { Icon } from "../../components/Icon";
import {
  Banner,
  Button,
  Card,
  Checkbox,
  ErrorState,
  Input,
  Money,
  PageHeader,
  RadioGroup,
  RefId,
  Select,
  SkeletonBlock,
  Textarea,
} from "../../components/ui";
import { formatLocal, formatUtc, toRfc3339Utc, viewerTimeZone } from "../../lib/datetime";
import { newIdempotencyKey } from "../../lib/idempotency";
import { amountValidationMessage, compareDecimal, maxWholeUnits, validateAmountInput } from "../../lib/money";
import { readJson, removeStorage, writeJson } from "../../lib/storage";
import { formatRate } from "./campaignCopy";
import {
  blockingIssues,
  categoryLabel,
  CategoryPicker,
  CreativeValidationPanel,
  fetchValidation,
  FormatPicker,
  InventoryHint,
  issueMessage,
  PublisherExclusions,
  unavailableCategories,
  useTargetingCategories,
} from "./campaignInputs";
import { resolveField, TimeField, type ResolvedTime, type TimeChoice } from "./scheduleFields";

// UX-DESIGN.md §2.3: a five-step wizard, each step a distinct reviewed
// artifact, then a submission that commits step by step (§6: "never
// presented as complete until every required step has actually committed").
//
// Submission order (§2.3 "Submitting, in order"; 0052: create versions →
// accept terms → review → promote):
//   1. POST /v1/campaigns                          — the shell, with its name (G-14)
//   2. POST /v1/campaigns/{id}/config-versions     — draft config, lifetime + daily budget (G-16)
//   3. POST /v1/campaigns/{id}/creative-versions   — draft creative (+ DNS challenge)
//   4. POST /v1/campaigns/{id}/terms/accept        — the terms version the advertiser was shown,
//                                                    bound to the two version ids (G-18)
// Every step carries an idempotency key bound to its exact request body and
// kept in sessionStorage, so a retry — even after a reload or a timeout
// whose outcome is unknown — reuses it and the server replays instead of
// creating a duplicate (G-21, campaign_commands.go runCampaignCommand). A
// step that is known to have succeeded is never re-sent.

type Format = CreativeFormatKey;

interface Draft {
  name: string;
  startLocal: string;
  startChoice: TimeChoice;
  endLocal: string;
  endChoice: TimeChoice;
  pricingModel: PricingModel | "";
  format: Format | "";
  /** targeting.categories: catalog keys (G-17). Empty is untargeted. */
  categories: string[];
  /** publisher_exclusions, kept with their display name for the review step. */
  excludedPublishers: TargetingPublisher[];
  currency: string;
  bid: string;
  lifetimeBudget: string;
  dailyBudget: string;
  budgetTimezone: string;
  /** Migration 0071: both empty means no cap; both filled or neither. */
  frequencyCapMaxImpressions: string;
  frequencyCapWindowHours: string;
  title: string;
  body: string;
  destinationUrl: string;
  /** The platform_terms_version_id the advertiser saw and accepted, or "" (G-18). */
  acceptedTermsId: string;
}

type StepKey = "campaign" | "config" | "creative" | "terms";

interface Progress {
  campaignId?: string;
  configVersionId?: string;
  creativeVersionId?: string;
  termsAcceptedAt?: string;
  platformFeeRate?: string;
  /** Idempotency key per step, bound to that step's request body. */
  keys?: Partial<Record<StepKey, { sig: string; key: string }>>;
}

const STEPS = ["Name & schedule", "Targeting & format", "Bid & budget", "Creative", "Review & submit"] as const;
const NAME_MAX = 120;

// No per-field character caps: the limit is the serialized envelope's
// 2,048 bytes (PLAN.md §5.1), which only the server measures exactly
// (POST /v1/creatives/validate, G-20). The step checks it live and
// Continue re-checks the exact text before moving on.

function emptyDraft(): Draft {
  return {
    name: "",
    startLocal: "",
    startChoice: "",
    endLocal: "",
    endChoice: "",
    pricingModel: "",
    format: "",
    categories: [],
    excludedPublishers: [],
    currency: "USD",
    bid: "",
    lifetimeBudget: "",
    dailyBudget: "",
    budgetTimezone: viewerTimeZone(),
    frequencyCapMaxImpressions: "",
    frequencyCapWindowHours: "",
    title: "",
    body: "",
    destinationUrl: "",
    acceptedTermsId: "",
  };
}

function destinationError(url: string): string | null {
  const v = url.trim();
  if (!v) return "Enter the destination URL.";
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return "Enter a full URL, starting with https://";
  }
  if (u.protocol !== "https:") return "The destination must use https://";
  const host = u.hostname;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith("[")) return "Use a domain name, not an IP address.";
  if (!host.includes(".") || host === "localhost") return "Use a public domain name.";
  if (u.username || u.password) return "The URL can't contain a username or password.";
  return null;
}

/** The exact decimal the user typed, normalised the way validateAmountInput accepts it. */
function amountValue(raw: string, currency: string): string {
  const v = validateAmountInput(raw, currency);
  return v.ok ? v.value : raw.trim();
}

/** "1234567" → "1,234,567" without going through a JS number. */
function groupDigits(n: string): string {
  return n.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** Server rejections that belong to a wizard step (campaign_commands.go + 0052 codes). */
const STEP_FOR_CODE: Record<string, { step: number; field: string }> = {
  invalid_name: { step: 0, field: "name" },
  schedule_end_before_start: { step: 0, field: "end" },
  invalid_bid_amount: { step: 2, field: "bid" },
  lifetime_budget_required: { step: 2, field: "lifetimeBudget" },
  invalid_lifetime_budget: { step: 2, field: "lifetimeBudget" },
  lifetime_budget_below_bid: { step: 2, field: "lifetimeBudget" },
  invalid_daily_budget: { step: 2, field: "dailyBudget" },
  daily_budget_exceeds_lifetime: { step: 2, field: "dailyBudget" },
  invalid_budget_timezone: { step: 2, field: "budgetTimezone" },
  invalid_frequency_cap: { step: 2, field: "frequencyCapMaxImpressions" },
  invalid_destination_url: { step: 3, field: "destinationUrl" },
  // 0061 targeting and creative rejections.
  invalid_targeting: { step: 1, field: "categories" },
  unknown_category: { step: 1, field: "categories" },
  category_not_cleared: { step: 1, field: "categories" },
  unknown_publisher: { step: 1, field: "publishers" },
  creative_invalid: { step: 3, field: "creative" },
};

export function CampaignWizard() {
  const session = useSession();
  const ep = useEndpoints();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const draftKey = `vads.campaignDraft.${session.me.party_id}`;

  const saved = useRef(
    (() => {
      const s = readJson<{ draft: Draft; step: number; progress: Progress }>("session", draftKey);
      // A fully committed submission is never resumed as a draft.
      return s?.progress?.termsAcceptedAt ? null : s;
    })(),
  );
  // Set once the submission completes, so the persistence effect below
  // can't write the finished draft back after it was cleared.
  const finished = useRef(false);
  const [draft, setDraft] = useState<Draft>({ ...emptyDraft(), ...saved.current?.draft });
  const [step, setStep] = useState<number>(saved.current?.step ?? 0);
  const [progress, setProgress] = useState<Progress>(saved.current?.progress ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<{ running: boolean; failedStep: number | null; error: ApiError | Error | null }>({
    running: false,
    failedStep: null,
    error: null,
  });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [checking, setChecking] = useState(false);
  const categories = useTargetingCategories();

  // G-18: the terms are shown before acceptance, pinned to their version id.
  const terms = useQuery({
    queryKey: ["commercial-terms", draft.currency],
    queryFn: () => ep.advertiser.getCurrentTerms(draft.currency),
    enabled: step === STEPS.length - 1,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (finished.current) return;
    writeJson("session", draftKey, { draft, step, progress });
  }, [draft, step, progress, draftKey]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  // The tick counts only for the terms version on screen: if the terms
  // shown change, it no longer means anything and a stale acceptance is
  // never sent.
  const termsAccepted = !!terms.data && draft.acceptedTermsId === terms.data.platform_terms_version_id;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const submitted = !!progress.campaignId;

  const tz = viewerTimeZone();
  const lockedShell = submitted;
  const lockedConfig = !!progress.configVersionId;
  const lockedCreative = !!progress.creativeVersionId;

  const start = resolveField(draft.startLocal, draft.startChoice, true);
  const end = resolveField(draft.endLocal, draft.endChoice, false);

  function validate(s: number): Record<string, string> {
    const e: Record<string, string> = {};
    if (s === 0) {
      const n = draft.name.trim();
      if (!n) e.name = "Give the campaign a name.";
      else if ([...n].length > NAME_MAX) e.name = `Keep the name to ${NAME_MAX} characters.`;
      if (!start.ok) e.start = start.error;
      if (!end.ok) e.end = end.error;
      if (start.ok && end.ok && start.instant && end.instant && end.instant <= start.instant)
        e.end = "The end must be after the start.";
    }
    if (s === 1) {
      if (!draft.pricingModel) e.pricingModel = "Choose a pricing model.";
      if (!draft.format) e.format = "Choose a format.";
      const blocked = unavailableCategories(draft.categories, categories.data?.categories);
      if (blocked.length)
        e.categories = `${blocked.map((k) => categoryLabel(categories.data?.categories, k)).join(", ")} can't be targeted right now. Remove ${blocked.length === 1 ? "it" : "them"} to continue.`;
    }
    if (s === 2) {
      const bid = validateAmountInput(draft.bid, draft.currency);
      if (!bid.ok) e.bid = amountValidationMessage(bid.reason, draft.currency);
      const life = validateAmountInput(draft.lifetimeBudget, draft.currency);
      if (!draft.lifetimeBudget.trim()) e.lifetimeBudget = "Enter a lifetime budget. It's required, and caps total spend.";
      else if (!life.ok) e.lifetimeBudget = amountValidationMessage(life.reason, draft.currency);
      else if (bid.ok && compareDecimal(life.value, bid.value) < 0)
        e.lifetimeBudget = "The lifetime budget must be at least one bid.";
      if (draft.dailyBudget.trim()) {
        const daily = validateAmountInput(draft.dailyBudget, draft.currency);
        if (!daily.ok) e.dailyBudget = amountValidationMessage(daily.reason, draft.currency);
        else if (life.ok && compareDecimal(daily.value, life.value) > 0)
          e.dailyBudget = "The daily budget can't be more than the lifetime budget.";
      }
      if (!draft.budgetTimezone) e.budgetTimezone = "Choose a time zone.";
      const capMax = draft.frequencyCapMaxImpressions.trim();
      const capWindow = draft.frequencyCapWindowHours.trim();
      if (!!capMax !== !!capWindow) {
        e.frequencyCapMaxImpressions = "Set both the impression cap and the window, or leave both empty.";
      } else if (capMax && capWindow) {
        const maxN = Number(capMax);
        const windowN = Number(capWindow);
        if (!Number.isInteger(maxN) || maxN < 1 || maxN > 100) {
          e.frequencyCapMaxImpressions = "Enter a whole number from 1 to 100.";
        }
        if (!Number.isInteger(windowN) || windowN < 1 || windowN > 720) {
          e.frequencyCapWindowHours = "Enter a whole number of hours from 1 to 720.";
        }
      }
    }
    if (s === 3) {
      if (!draft.title.trim()) e.title = "Enter a title.";
      if (!draft.body.trim()) e.body = "Enter the body text.";
      const de = destinationError(draft.destinationUrl);
      if (de) e.destinationUrl = de;
    }
    if (s === 4 && !termsAccepted && !progress.termsAcceptedAt) e.acceptTerms = "Accept the commercial terms to submit.";
    return e;
  }

  function creativeRequest() {
    return { title: draft.title.trim(), body: draft.body.trim(), format: draft.format, destination_url: draft.destinationUrl.trim() };
  }

  async function next(e?: FormEvent) {
    e?.preventDefault();
    const errs = validate(step);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (step === 3 && !lockedCreative) {
      // G-20: the server's check of exactly this text decides. Errors
      // block; warnings and review flags don't.
      setChecking(true);
      try {
        const v = await fetchValidation(qc, ep, creativeRequest());
        const blocking = blockingIssues(v);
        if (blocking.length) {
          const fe: Record<string, string> = {};
          for (const i of blocking) {
            const field =
              i.field === "title" ? "title" : i.field === "body" ? "body" : i.field === "destination_url" ? "destinationUrl" : "creative";
            const msg = issueMessage(i, v.destination.host);
            fe[field] = fe[field] ? `${fe[field]} ${msg}` : msg;
          }
          setErrors(fe);
          return;
        }
      } catch {
        // Couldn't check now: the creative write runs the same static
        // checks and refuses with creative_invalid, so moving on is safe.
      } finally {
        setChecking(false);
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  /** The step's key: reused while its request body is unchanged, new when it changes. */
  function keyFor(p: Progress, k: StepKey, body: unknown): [Progress, string] {
    const sig = JSON.stringify(body);
    const cur = p.keys?.[k];
    if (cur && cur.sig === sig) return [p, cur.key];
    const key = newIdempotencyKey();
    return [{ ...p, keys: { ...p.keys, [k]: { sig, key } } }, key];
  }

  async function submit() {
    for (let s = 0; s < STEPS.length; s++) {
      const errs = validate(s);
      if (Object.keys(errs).length) {
        setErrors(errs);
        setStep(s);
        return;
      }
    }
    setSubmitState({ running: true, failedStep: null, error: null });
    let p: Progress = { ...progress };
    let current = 0;
    let currentKey: StepKey = "campaign";
    try {
      if (!p.campaignId) {
        current = 0;
        currentKey = "campaign";
        const body = {
          name: draft.name.trim(),
          schedule_start_utc: toRfc3339Utc((start as { instant: Date }).instant),
          schedule_end_utc: end.ok && end.instant ? toRfc3339Utc(end.instant) : null,
        };
        let key: string;
        [p, key] = keyFor(p, "campaign", body);
        setProgress(p);
        const res = await ep.advertiser.createCampaign(body, { idempotencyKey: key });
        p = { ...p, campaignId: res.campaign_id };
        setProgress(p);
      }
      const campaignId = p.campaignId!;
      if (!p.configVersionId) {
        current = 1;
        currentKey = "config";
        const body = {
          currency: draft.currency,
          bid_type: draft.pricingModel as PricingModel,
          bid_amount: amountValue(draft.bid, draft.currency),
          targeting: draft.categories.length ? { categories: [...draft.categories] } : {},
          publisher_exclusions: draft.excludedPublishers.map((p) => p.publisher_id),
          category_exclusions: [],
          budget_timezone: draft.budgetTimezone,
          lifetime_budget: amountValue(draft.lifetimeBudget, draft.currency),
          daily_budget: draft.dailyBudget.trim() ? amountValue(draft.dailyBudget, draft.currency) : null,
          frequency_cap:
            draft.frequencyCapMaxImpressions.trim() && draft.frequencyCapWindowHours.trim()
              ? { max_impressions: Number(draft.frequencyCapMaxImpressions), window_hours: Number(draft.frequencyCapWindowHours) }
              : null,
        };
        let key: string;
        [p, key] = keyFor(p, "config", body);
        setProgress(p);
        const res = await ep.advertiser.createConfigVersion(campaignId, body, { idempotencyKey: key });
        p = { ...p, configVersionId: res.config_version_id };
        setProgress(p);
      }
      if (!p.creativeVersionId) {
        current = 2;
        currentKey = "creative";
        const body = { ...creativeRequest(), format: draft.format as Format };
        let key: string;
        [p, key] = keyFor(p, "creative", body);
        setProgress(p);
        const res = await ep.advertiser.createCreativeVersion(campaignId, body, { idempotencyKey: key });
        p = { ...p, creativeVersionId: res.creative_version_id };
        setProgress(p);
      }
      if (!p.termsAcceptedAt) {
        current = 3;
        currentKey = "terms";
        const body = {
          platform_terms_version_id: draft.acceptedTermsId,
          config_version_id: p.configVersionId!,
          creative_version_id: p.creativeVersionId!,
        };
        let key: string;
        [p, key] = keyFor(p, "terms", body);
        setProgress(p);
        const res = await ep.advertiser.acceptTerms(campaignId, body, { idempotencyKey: key });
        p = { ...p, termsAcceptedAt: res.accepted_at, platformFeeRate: res.platform_fee_rate };
        setProgress(p);
      }
      finished.current = true;
      setSubmitState({ running: false, failedStep: null, error: null });
      removeStorage("session", draftKey);
      navigate(`/advertiser/campaigns/${campaignId}?submitted=1`);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if (error instanceof ApiError) {
        if (error.code === "terms_version_changed") {
          // The terms changed between showing and accepting (0052:835-841):
          // show the new ones and ask again. Nothing stale is recorded.
          const fresh = error.detail?.current_terms as CurrentTerms | undefined;
          if (fresh?.platform_terms_version_id) qc.setQueryData(["commercial-terms", draft.currency], fresh);
          else void terms.refetch();
          setDraft((d) => ({ ...d, acceptedTermsId: "" }));
        }
        if (error.code === "idempotency_key_reused") {
          // The server holds a different request under this step's key. Drop
          // the key so an explicit retry is sent as a new request.
          const keys = { ...p.keys };
          delete keys[currentKey];
          p = { ...p, keys };
          setProgress(p);
        }
        const target = error.code ? STEP_FOR_CODE[error.code] : undefined;
        if (target) setErrors((e) => ({ ...e, [target.field]: error.message }));
      }
      setSubmitState({ running: false, failedStep: current, error });
    }
  }

  function discard() {
    removeStorage("session", draftKey);
    setDraft(emptyDraft());
    setProgress({});
    setStep(0);
    setErrors({});
    setSubmitState({ running: false, failedStep: null, error: null });
  }


  return (
    <div className="stack-lg">
      <PageHeader
        title="New campaign"
        back={{ to: "/advertiser/campaigns", label: "Campaigns" }}
        description="Each step becomes a separately reviewed part of your campaign. Your progress is kept in this tab if your session times out."
      />

      <nav aria-label="Campaign builder steps">
        <ol className="steps">
          {STEPS.map((label, i) => (
            <li key={label} className={["step", i < step ? "step-done" : ""].join(" ")} aria-current={i === step ? "step" : undefined}>
              {i < step ? <Icon name="check" /> : <span aria-hidden="true">{i + 1}</span>}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ padding: 0, minHeight: 0 }}
                onClick={() => (i < step || submitted ? setStep(i) : undefined)}
                disabled={i > step && !submitted}
              >
                {label}
                {i < step ? (
                  <>
                    {" "}
                    <span className="visually-hidden">(completed)</span>
                  </>
                ) : null}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {submitted ? (
        <Banner tone="info" title="Submission in progress">
          Part of this campaign has already been created. The remaining steps are retried from where they stopped — nothing that
          already went through is sent again.
        </Banner>
      ) : null}

      <form onSubmit={(e) => (e.preventDefault(), void (step === STEPS.length - 1 ? submit() : next()))} noValidate>
        <Card>
          <div className="stack">
            <h2 ref={headingRef} tabIndex={-1} style={{ outline: "none" }}>
              {STEPS[step]}
            </h2>

            {step === 0 ? (
              <div className="stack">
                <div style={{ maxWidth: 480 }}>
                  <Input
                    label="Campaign name"
                    value={draft.name}
                    onChange={(e) => set("name", e.target.value)}
                    error={errors.name}
                    hint={`${[...draft.name.trim()].length}/${NAME_MAX} characters. Only you and your team see it; you can rename it later.`}
                    disabled={lockedShell}
                  />
                </div>
                <p className="small muted">
                  Times are in your time zone, <strong>{tz}</strong>, and stored as exact UTC instants. The campaign serves from
                  the start instant, up to but not including the end instant.
                </p>
                <div className="grid-2">
                  <TimeField
                    label="Start"
                    value={draft.startLocal}
                    onChange={(v) => setDraft((d) => ({ ...d, startLocal: v, startChoice: "" }))}
                    resolved={start}
                    choice={draft.startChoice}
                    onChoice={(c) => set("startChoice", c)}
                    error={errors.start}
                    disabled={lockedShell}
                  />
                  <TimeField
                    label="End"
                    optional
                    value={draft.endLocal}
                    onChange={(v) => setDraft((d) => ({ ...d, endLocal: v, endChoice: "" }))}
                    resolved={end}
                    choice={draft.endChoice}
                    onChoice={(c) => set("endChoice", c)}
                    error={errors.end}
                    hint="Leave empty to run until you end it."
                    disabled={lockedShell}
                  />
                </div>
                <p className="xs muted">
                  Schedule isn't part of review. Once submitted, changing it takes effect immediately and needs you to confirm it's you.
                </p>
              </div>
            ) : null}

            {step === 1 ? (
              <div className="stack">
                <CategoryPicker
                  value={draft.categories}
                  onChange={(v) => set("categories", v)}
                  disabled={lockedConfig}
                  error={errors.categories}
                />
                <PublisherExclusions
                  value={draft.excludedPublishers}
                  onChange={(v) => set("excludedPublishers", v)}
                  disabled={lockedConfig}
                  error={errors.publishers}
                />
                <RadioGroup<PricingModel>
                  legend="Pricing model"
                  name="pricing_model"
                  value={draft.pricingModel}
                  onChange={(v) => (lockedConfig ? undefined : set("pricingModel", v))}
                  options={[
                    { value: "cpm", label: "CPM — per thousand impressions", hint: "Charged when an ad is shown and acknowledged by the publisher's client." },
                    {
                      value: "cpe",
                      label: "CPE — per engagement",
                      hint: "Charged per captured engagement. While CPE billing is in evaluation mode, CPE activity is recorded but not billed.",
                    },
                  ]}
                />
                {errors.pricingModel ? <div className="field-error" role="alert">{errors.pricingModel}</div> : null}
                <p className="small muted">
                  A campaign has exactly one pricing model. To reach both CPM and CPE inventory, create two campaigns.
                </p>
                <InventoryHint categories={draft.categories} pricingModel={draft.pricingModel} />
                <FormatPicker value={draft.format} onChange={(v) => set("format", v)} disabled={lockedCreative} />
                {errors.format ? <div className="field-error" role="alert">{errors.format}</div> : null}
                {draft.format ? <FormatPreview format={draft.format} title={draft.title} body={draft.body} /> : null}
              </div>
            ) : null}

            {step === 2 ? (
              <BudgetStep draft={draft} set={set} errors={errors} locked={lockedConfig} />
            ) : null}

            {step === 3 ? (
              <div className="stack">
                <Input
                  label="Title"
                  value={draft.title}
                  onChange={(e) => set("title", e.target.value)}
                  error={errors.title}
                  hint={`${[...draft.title].length} characters`}
                  disabled={lockedCreative}
                />
                <Textarea
                  label="Body"
                  value={draft.body}
                  onChange={(e) => set("body", e.target.value)}
                  error={errors.body}
                  hint={`${[...draft.body].length} characters. Describe; don't instruct — text that reads as a command to an AI tool is flagged for review.`}
                  disabled={lockedCreative}
                />
                <Input
                  label="Destination URL"
                  type="url"
                  value={draft.destinationUrl}
                  onChange={(e) => set("destinationUrl", e.target.value)}
                  error={errors.destinationUrl}
                  hint="Any https URL, including a page you don't control the domain of (a social profile, a marketplace listing, an app store page, and similar). Link directly to the page — shortened or forwarding links aren't allowed."
                  disabled={lockedCreative}
                />
                <div className="stack-sm">
                  <span className="field-label">Disclosure</span>
                  <p className="small">
                    Every ad carries <code>disclosure: "sponsored"</code> in its envelope — exact label text. Actual
                    rendering and placement are controlled by the publisher's client and aren't shown here. It can't be edited or
                    removed.
                  </p>
                </div>
                <CreativeValidationPanel req={creativeRequest()} enabled={!lockedCreative} />
                {errors.creative ? (
                  <div className="field-error" role="alert">
                    <Icon name="error" className="field-error-icon" />
                    <span>{errors.creative}</span>
                  </div>
                ) : null}
                <p className="xs muted">Call-to-action text isn't a creative field yet (API-GAPS.md G-20); the ad links to the destination URL.</p>
              </div>
            ) : null}

            {step === 4 ? (
              <ReviewStep
                draft={draft}
                start={start}
                end={end}
                errors={errors}
                categoryLabels={draft.categories.map((k) => categoryLabel(categories.data?.categories, k))}
                terms={terms.data}
                termsError={terms.error}
                termsLoading={terms.isPending}
                onRetryTerms={() => void terms.refetch()}
                onAccept={(v) => set("acceptedTermsId", v && terms.data ? terms.data.platform_terms_version_id : "")}
                accepted={termsAccepted}
                lockedAccepted={!!progress.termsAcceptedAt}
              />
            ) : null}

            {submitState.running || submitState.failedStep !== null || submitted ? (
              <SubmissionProgress progress={progress} running={submitState.running} failedStep={submitState.failedStep} error={submitState.error} />
            ) : null}

            <div className="row-between">
              <div className="row">
                {step > 0 ? (
                  <Button variant="secondary" onClick={() => setStep((s) => s - 1)} disabled={submitState.running}>
                    Back
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={discard} disabled={submitState.running || submitted}>
                  Discard draft
                </Button>
              </div>
              {step < STEPS.length - 1 ? (
                <Button type="submit" loading={checking}>
                  Continue
                </Button>
              ) : (
                <Button type="submit" loading={submitState.running} disabled={!termsAccepted && !progress.termsAcceptedAt}>
                  {submitState.failedStep !== null ? "Retry remaining steps" : "Submit for review"}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </form>
      {submitted && progress.campaignId ? (
        <p className="small">
          <Link to={`/advertiser/campaigns/${progress.campaignId}`}>Open this campaign's status page</Link>
        </p>
      ) : null}
    </div>
  );
}

function BudgetStep({
  draft,
  set,
  errors,
  locked,
}: {
  draft: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  errors: Record<string, string>;
  locked: boolean;
}) {
  const cpm = draft.pricingModel === "cpm";
  const bid = validateAmountInput(draft.bid, draft.currency);
  const life = validateAmountInput(draft.lifetimeBudget, draft.currency);
  const daily = validateAmountInput(draft.dailyBudget, draft.currency);
  const unit = cpm ? "impressions" : "engagements";
  const lifeN = bid.ok && life.ok ? maxWholeUnits(life.value, bid.value, cpm ? 1000 : 1) : null;
  const dailyN = bid.ok && daily.ok ? maxWholeUnits(daily.value, bid.value, cpm ? 1000 : 1) : null;
  return (
    <div className="stack">
      {locked ? (
        <Banner tone="neutral">This version was already created, so its bid and budget can't change in this submission.</Banner>
      ) : null}
      <div className="grid-2">
        <Select
          label="Currency"
          value={draft.currency}
          onChange={(e) => set("currency", e.target.value)}
          options={[{ value: "USD", label: "USD — US dollar" }]}
          hint="The platform's operating currency."
          disabled={locked}
        />
        <Input
          label={cpm ? "Bid per 1,000 impressions" : "Bid per engagement"}
          inputMode="decimal"
          value={draft.bid}
          onChange={(e) => set("bid", e.target.value)}
          error={errors.bid}
          hint={`Exact amount in ${draft.currency}, up to 2 decimal places.`}
          disabled={locked}
        />
      </div>
      <div className="grid-2">
        <Input
          label="Lifetime budget"
          inputMode="decimal"
          value={draft.lifetimeBudget}
          onChange={(e) => set("lifetimeBudget", e.target.value)}
          error={errors.lifetimeBudget}
          hint={`Required. The most this campaign can ever spend, in ${draft.currency}.`}
          disabled={locked}
        />
        <Input
          label="Daily budget"
          optional
          inputMode="decimal"
          value={draft.dailyBudget}
          onChange={(e) => set("dailyBudget", e.target.value)}
          error={errors.dailyBudget}
          hint="At most the lifetime budget. Enforced on every reservation, per day in the budget time zone."
          disabled={locked}
        />
      </div>
      <Input
        label="Budget time zone"
        value={draft.budgetTimezone}
        onChange={(e) => set("budgetTimezone", e.target.value)}
        error={errors.budgetTimezone}
        hint="IANA name, e.g. America/New_York. Daily budget periods roll over at midnight here, and it can't change once budgets are in use."
        disabled={locked}
      />
      <div className="grid-2">
        <Input
          label="Frequency cap: impressions"
          optional
          inputMode="numeric"
          value={draft.frequencyCapMaxImpressions}
          onChange={(e) => set("frequencyCapMaxImpressions", e.target.value)}
          error={errors.frequencyCapMaxImpressions}
          hint="1-100. The most times one publisher session is shown this ad within the window below."
          disabled={locked}
        />
        <Input
          label="Frequency cap: window (hours)"
          optional
          inputMode="numeric"
          value={draft.frequencyCapWindowHours}
          onChange={(e) => set("frequencyCapWindowHours", e.target.value)}
          error={errors.frequencyCapWindowHours}
          hint="1-720 (30 days). Leave both cap fields empty for no cap."
          disabled={locked}
        />
      </div>
      <p className="xs muted">
        The session identifier this counts against is publisher-supplied and untrusted: it's never a reason to charge
        anything, only a reason to skip this campaign in favor of the next bidder. Requests with no session identifier
        are never capped.
      </p>
      <div className="small muted" role="status">
        {lifeN !== null ? (
          <p>
            Lifetime: up to <strong>{groupDigits(lifeN)}</strong> {unit}
            {dailyN !== null ? (
              <>
                , and up to <strong>{groupDigits(dailyN)}</strong> a day
              </>
            ) : null}
            .
          </p>
        ) : null}
        <p>
          {cpm
            ? "Up to N = (budget ÷ bid) × 1,000 impressions — each impression consumes bid ÷ 1,000."
            : "Up to N = budget ÷ bid engagements — each captured engagement consumes exactly the bid."}{" "}
          “Up to”, not “approximately”: it is a hard ceiling, with no fill-rate data behind it. The platform fee and publisher share
          are taken out of the captured amount, not added on top.
        </p>
        <p>After the campaign is live, raising either budget needs you to confirm it's you; lowering one can't go below what's already spent or reserved.</p>
      </div>
    </div>
  );
}

function FormatPreview({ format, title, body }: { format: Format; title: string; body: string }) {
  return (
    <figure className="stack-sm" style={{ margin: 0 }}>
      <figcaption className="small muted">Illustrative — actual rendering is controlled by the publisher's client.</figcaption>
      <div className="card" style={{ background: "var(--color-surface-sunken)", boxShadow: "none" }}>
        <div className="stack-sm small">
          <span className="muted mono xs">…tool result…</span>
          {format === "sponsored_listing" ? (
            <div>
              <span className="pill pill-neutral">Sponsored</span> <strong>{title || "Your title"}</strong>
              <p>{body || "Your body text."}</p>
            </div>
          ) : (
            <p>
              <span className="pill pill-neutral">Sponsored</span> {body || "Your body text."}
            </p>
          )}
        </div>
      </div>
    </figure>
  );
}

function ReviewStep({
  draft,
  start,
  end,
  errors,
  categoryLabels,
  terms,
  termsError,
  termsLoading,
  onRetryTerms,
  onAccept,
  accepted,
  lockedAccepted,
}: {
  draft: Draft;
  start: ResolvedTime;
  end: ResolvedTime;
  errors: Record<string, string>;
  categoryLabels: string[];
  terms: CurrentTerms | undefined;
  termsError: Error | null;
  termsLoading: boolean;
  onRetryTerms: () => void;
  onAccept: (v: boolean) => void;
  accepted: boolean;
  lockedAccepted: boolean;
}) {
  const per = draft.pricingModel === "cpm" ? "per 1,000 impressions" : "per engagement";
  return (
    <div className="stack">
      <dl className="definition-list">
        <dt>Name</dt>
        <dd>{draft.name.trim() || "—"}</dd>
        <dt>Start</dt>
        <dd>{start.ok && start.instant ? `${formatLocal(start.instant)} · ${formatUtc(start.instant)}` : "—"}</dd>
        <dt>End</dt>
        <dd>{end.ok && end.instant ? `${formatLocal(end.instant)} · ${formatUtc(end.instant)}` : "No end date"}</dd>
        <dt>Pricing model</dt>
        <dd>{draft.pricingModel.toUpperCase() || "—"}</dd>
        <dt>Format</dt>
        <dd>{draft.format === "sponsored_listing" ? "Sponsored listing" : draft.format === "contextual_note" ? "Contextual note" : "—"}</dd>
        <dt>Targeting</dt>
        <dd>{categoryLabels.length ? categoryLabels.join(", ") : "No category narrowing: serves in every context"}</dd>
        <dt>Excluded publishers</dt>
        <dd>{draft.excludedPublishers.length ? draft.excludedPublishers.map((p) => p.display_name).join(", ") : "None"}</dd>
        <dt>Bid</dt>
        <dd>
          {draft.bid ? <Money value={amountValue(draft.bid, draft.currency)} currency={draft.currency} /> : "—"} {per}
        </dd>
        <dt>Lifetime budget</dt>
        <dd>{draft.lifetimeBudget ? <Money value={amountValue(draft.lifetimeBudget, draft.currency)} currency={draft.currency} /> : "—"}</dd>
        <dt>Daily budget</dt>
        <dd>
          {draft.dailyBudget.trim() ? <Money value={amountValue(draft.dailyBudget, draft.currency)} currency={draft.currency} /> : "No daily cap"}
        </dd>
        <dt>Budget time zone</dt>
        <dd>{draft.budgetTimezone}</dd>
        <dt>Frequency cap</dt>
        <dd>
          {draft.frequencyCapMaxImpressions.trim() && draft.frequencyCapWindowHours.trim()
            ? `${draft.frequencyCapMaxImpressions} impressions per session, every ${draft.frequencyCapWindowHours} hours`
            : "None"}
        </dd>
        <dt>Title</dt>
        <dd>{draft.title}</dd>
        <dt>Body</dt>
        <dd>{draft.body}</dd>
        <dt>Destination</dt>
        <dd className="break-all">{draft.destinationUrl}</dd>
      </dl>
      <Card level={3} title="Commercial terms" subtitle="Recorded against the exact config and creative versions this submission creates.">
        {termsLoading ? (
          <SkeletonBlock label="Loading the current commercial terms" lines={3} />
        ) : termsError ? (
          <ErrorState error={termsError} onRetry={onRetryTerms} />
        ) : terms ? (
          <dl className="definition-list">
            <dt>Rate model</dt>
            <dd>One standardized network-wide rate, the same whichever publisher serves the ad.</dd>
            <dt>Platform fee</dt>
            <dd>
              <strong>{formatRate(terms.platform_fee_rate)}</strong> of each captured amount
            </dd>
            <dt>Publisher revenue share</dt>
            <dd>{formatRate(terms.publisher_share_rate)}</dd>
            <dt>Currency</dt>
            <dd>{terms.currency}</dd>
            <dt>Rounding</dt>
            <dd>
              <code>{terms.rounding_rule}</code> to the currency's minor units, applied only when statements are issued.
            </dd>
            <dt>Terms version</dt>
            <dd>
              <code className="xs">{terms.platform_terms_version_id}</code> · in effect since {formatLocal(terms.effective_from)}
            </dd>
          </dl>
        ) : null}
      </Card>
      <Checkbox
        label="I accept these commercial terms for this campaign"
        checked={accepted || lockedAccepted}
        onChange={(e) => onAccept(e.target.checked)}
        disabled={!terms || lockedAccepted}
        hint="Your acceptance is pinned to the terms version shown above. If the terms change before your campaign goes live, you'll be asked to accept again."
      />
      {errors.acceptTerms ? <div className="field-error" role="alert">{errors.acceptTerms}</div> : null}
    </div>
  );
}

const SUBMIT_STEPS = [
  "Create campaign",
  "Save targeting, bid & budget as a draft version",
  "Save creative as a draft version",
  "Record terms acceptance",
] as const;

function SubmissionProgress({
  progress,
  running,
  failedStep,
  error,
}: {
  progress: Progress;
  running: boolean;
  failedStep: number | null;
  error: Error | null;
}) {
  const done = [!!progress.campaignId, !!progress.configVersionId, !!progress.creativeVersionId, !!progress.termsAcceptedAt];
  const firstPending = done.findIndex((d) => !d);
  const api = error instanceof ApiError ? error : null;
  return (
    <div className="stack-sm" role="status" aria-live="polite">
      <h3>Submission</h3>
      <ul className="progress-list">
        {SUBMIT_STEPS.map((label, i) => {
          const state = done[i] ? "done" : failedStep === i ? "failed" : running && firstPending === i ? "running" : "pending";
          return (
            <li key={label} className="progress-item">
              <Icon name={state === "done" ? "checkCircle" : state === "failed" ? "error" : state === "running" ? "clock" : "circle"} />
              <span>
                {label} — <strong>{state === "done" ? "done" : state === "failed" ? "failed" : state === "running" ? "in progress" : "not started"}</strong>
              </span>
            </li>
          );
        })}
      </ul>
      {failedStep !== null && error ? (
        <Banner tone="error" title={`“${SUBMIT_STEPS[failedStep]}” didn't go through`}>
          <div className="stack-sm">
            <span>{error.message}</span>
            {api?.code === "terms_version_changed" ? (
              <span className="small">The new terms are shown above. Tick the box again to accept them, then retry.</span>
            ) : null}
            {api?.code && STEP_FOR_CODE[api.code] ? (
              <span className="small">Go back to “{STEPS[STEP_FOR_CODE[api.code]!.step]}” to fix it, then retry.</span>
            ) : null}
            {api?.code === "idempotency_key_reused" ? (
              <span className="small">Retrying now sends this step as a new request.</span>
            ) : null}
            {api?.retryable ? (
              <span className="small">
                We can't tell whether this step reached the server. Retrying is safe: it reuses the same reference, so a step that
                already went through is recognized and not created twice.
              </span>
            ) : null}
            {api?.idempotencyKey ? <RefId value={api.idempotencyKey} /> : null}
            {progress.campaignId ? (
              <span className="small">
                Campaign <code>{progress.campaignId}</code> exists and is waiting for the remaining steps.
              </span>
            ) : null}
          </div>
        </Banner>
      ) : null}
    </div>
  );
}
